import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Save } from "lucide-react";
import { kpiSchema } from "@/schemas/kpiSchemas";
import logger from '@/lib/logger';

interface KPI {
  id: string;
  metric_name: string;
  threshold_value: number;
  condition: string;
  notification_enabled: boolean;
}

export const KPIManager = () => {
  const { t } = useLanguage();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKPI, setNewKPI] = useState<{
    metric_name: string;
    threshold_value: number;
    condition: "greater_than" | "less_than" | "equal_to";
    notification_enabled: boolean;
  }>({
    metric_name: "",
    threshold_value: 0,
    condition: "greater_than",
    notification_enabled: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      const { data, error } = await api
        .from("supervisor_kpis")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      setKpis(data || []);
    } catch (error) {
      logger.error("Error fetching KPIs:", error);
      toast({
        title: t.settings.error || "Error",
        description: t.settings.errorLoadingKPIs || "Error loading KPIs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addKPI = async () => {
    setErrors({});
    
    // Validate KPI data
    const validation = kpiSchema.safeParse(newKPI);
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0].toString()] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast({
        title: t.settings.error || "Error",
        description: "Por favor corrija los errores en el formulario",
        variant: "destructive",
      });
      return;
    }

    try {
      const user = getCurrentUser();
      if (!user) return;

      const { error } = await api
        .from("supervisor_kpis")
        .insert({
          user_id: user.id,
          metric_name: validation.data.metric_name,
          threshold_value: validation.data.threshold_value,
          condition: validation.data.condition,
          notification_enabled: validation.data.notification_enabled,
        });

      if (error) throw error;

      toast({
        title: t.settings.success || "Success",
        description: t.settings.kpiAdded || "KPI added successfully",
      });

      setNewKPI({
        metric_name: "",
        threshold_value: 0,
        condition: "greater_than",
        notification_enabled: true,
      });
      setErrors({});

      fetchKPIs();
    } catch (error) {
      logger.error("Error adding KPI:", error);
      toast({
        title: t.settings.error || "Error",
        description: t.settings.errorAddingKPI || "Error adding KPI",
        variant: "destructive",
      });
    }
  };

  const updateKPI = async (id: string, updates: Partial<KPI>) => {
    try {
      const { error } = await api
        .from("supervisor_kpis")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: t.settings.success || "Success",
        description: t.settings.kpiUpdated || "KPI updated successfully",
      });

      fetchKPIs();
    } catch (error) {
      logger.error("Error updating KPI:", error);
      toast({
        title: t.settings.error || "Error",
        description: t.settings.errorUpdatingKPI || "Error updating KPI",
        variant: "destructive",
      });
    }
  };

  const deleteKPI = async (id: string) => {
    try {
      const { error } = await api
        .from("supervisor_kpis")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: t.settings.success || "Success",
        description: t.settings.kpiDeleted || "KPI deleted successfully",
      });

      fetchKPIs();
    } catch (error) {
      logger.error("Error deleting KPI:", error);
      toast({
        title: t.settings.error || "Error",
        description: t.settings.errorDeletingKPI || "Error deleting KPI",
        variant: "destructive",
      });
    }
  };

  const getMetricName = (metric: string) => {
    const names: Record<string, string> = {
      active_orders: t.dashboard?.activeOrders || "Active Orders",
      in_progress_processes: t.dashboard?.inProgressProcesses || "In Progress",
      active_operators: t.dashboard?.activeOperators || "Active Operators",
      completion_rate: t.dashboard?.completionRate || "Completion Rate",
    };
    return names[metric] || metric;
  };

  const getConditionName = (condition: string) => {
    const conditions: Record<string, string> = {
      greater_than: t.settings?.greaterThan || "Greater than",
      less_than: t.settings?.lessThan || "Less than",
      equal_to: t.settings?.equalTo || "Equal to",
    };
    return conditions[condition] || condition;
  };

  if (loading) {
    return <div>{t.common?.loading || "Loading..."}</div>;
  }

  return (
    <Card className="p-6">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-foreground">
          {t.settings?.customKPIs || "Custom KPIs"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add new KPI */}
        <div className="space-y-4 p-4 border border-border rounded-lg bg-muted/30">
          <h4 className="font-medium text-foreground">{t.settings?.addNewKPI || "Add New KPI"}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>{t.settings?.metric || "Metric"}</Label>
              <Select
                value={newKPI.metric_name}
                onValueChange={(value) => setNewKPI({ ...newKPI, metric_name: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.settings?.selectMetric || "Select metric"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active_orders">{t.dashboard?.activeOrders || "Active Orders"}</SelectItem>
                  <SelectItem value="in_progress_processes">{t.dashboard?.inProgressProcesses || "In Progress"}</SelectItem>
                  <SelectItem value="active_operators">{t.dashboard?.activeOperators || "Active Operators"}</SelectItem>
                  <SelectItem value="completion_rate">{t.dashboard?.completionRate || "Completion Rate"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t.settings?.condition || "Condition"}</Label>
              <Select
                value={newKPI.condition}
                onValueChange={(value: "greater_than" | "less_than" | "equal_to") => 
                  setNewKPI({ ...newKPI, condition: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="greater_than">{t.settings?.greaterThan || "Greater than"}</SelectItem>
                  <SelectItem value="less_than">{t.settings?.lessThan || "Less than"}</SelectItem>
                  <SelectItem value="equal_to">{t.settings?.equalTo || "Equal to"}</SelectItem>
                </SelectContent>
              </Select>
              {errors.condition && (
                <p className="text-sm text-destructive mt-1">{errors.condition}</p>
              )}
            </div>

            <div>
              <Label>{t.settings?.threshold || "Threshold"}</Label>
              <Input
                type="number"
                value={newKPI.threshold_value}
                onChange={(e) => setNewKPI({ ...newKPI, threshold_value: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="flex items-end">
              <Button onClick={addKPI} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                {t.settings?.add || "Add"}
              </Button>
            </div>
          </div>
        </div>

        {/* Existing KPIs */}
        <div className="space-y-3">
          {kpis.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              {t.settings?.noKPIsConfigured || "No KPIs configured yet"}
            </p>
          ) : (
            kpis.map((kpi) => (
              <div key={kpi.id} className="flex items-center justify-between p-4 border border-border rounded-lg bg-card">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">{t.settings?.metric || "Metric"}</Label>
                    <p className="font-medium">{getMetricName(kpi.metric_name)}</p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">{t.settings?.condition || "Condition"}</Label>
                    <p className="font-medium">{getConditionName(kpi.condition)}</p>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">{t.settings?.threshold || "Threshold"}</Label>
                    <Input
                      type="number"
                      value={kpi.threshold_value}
                      onChange={(e) => updateKPI(kpi.id, { threshold_value: parseInt(e.target.value) || 0 })}
                      className="w-24"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={kpi.notification_enabled}
                      onCheckedChange={(checked) => updateKPI(kpi.id, { notification_enabled: checked })}
                    />
                    <Label className="text-sm">{t.settings?.notifications || "Notifications"}</Label>
                  </div>
                </div>

                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => deleteKPI(kpi.id)}
                  className="ml-4"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
